import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Circle, Utensils, Users, Save, Download, FileDown, Mail, AlertTriangle } from "lucide-react";

const STATES = [
  { value: "", label: "— Not marked —", tone: "slate" },
  { value: "present", label: "Present", tone: "emerald" },
  { value: "late", label: "Late", tone: "amber" },
  { value: "left_early", label: "Left early", tone: "amber" },
  { value: "notified_absence", label: "Notified absence", tone: "slate" },
  { value: "no_show", label: "No-show", tone: "rose" },
  { value: "withdrew", label: "Withdrew", tone: "rose" },
];
const STATE_TONE = Object.fromEntries(STATES.map((s) => [s.value, s.tone]));
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

export default function Attendance() {
  const confirm = useConfirm();
  const [days, setDays] = useState([]);
  const [dayId, setDayId] = useState("");
  const [dayMeta, setDayMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  // Load days on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/days");
        const sorted = (data || []).sort((a, b) => (a.day_number || 0) - (b.day_number || 0));
        setDays(sorted);
        if (sorted.length && !dayId) setDayId(sorted[0].id);
      } catch {
        toast.error("Could not load sessions");
      }
    })();
  }, [dayId]);

  const loadAttendance = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance/${id}`);
      setDayMeta(data.day || null);
      setRows(data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load attendance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAttendance(dayId); }, [dayId, loadAttendance]);

  const stats = useMemo(() => {
    const total = rows.length;
    const present = rows.filter((r) => ["present","late","left_early"].includes(r.state)).length;
    const withOrders = rows.filter((r) => r.state && (r.food_order || "").trim()).length;
    const noShows = rows.filter((r) => r.state === "no_show").length;
    const pendingFollowups = rows.filter((r) => r.state === "no_show" && !r.followup_sent_at && r.email).length;
    return { total, present, absent: total - present, withOrders, noShows, pendingFollowups };
  }, [rows]);

  const setState = (rid, val) => {
    setRows((prev) => prev.map((r) => r.roster_id === rid
      ? { ...r, state: val, present: ["present","late","left_early"].includes(val) }
      : r));
  };

  const markAllPresent = () => {
    setRows((prev) => prev.map((r) => ({ ...r, state: "present", present: true })));
  };

  const clearAll = () => {
    setRows((prev) => prev.map((r) => ({ ...r, state: "", present: false, food_order: "" })));
  };

  const sendFollowups = async () => {
    if (!dayId) return;
    const recipients = rows.filter((r) => r.state === "no_show" && !r.followup_sent_at && r.email);
    if (!recipients.length) { toast.info("No unsent no-show follow-ups for this session"); return; }
    const names = recipients.map((r) => `${r.full_name} (${r.email})`).join(", ");
    if (!(await confirm({
      title: `Email ${recipients.length} student${recipients.length === 1 ? "" : "s"} a "We missed you" follow-up?`,
      description: `${dayMeta?.title || "This session"}: ${names}. The email asks why they missed the session and whether they're still committed. This cannot be undone.`,
      confirmText: `Send ${recipients.length} email${recipients.length === 1 ? "" : "s"}`,
      destructive: true,
    }))) return;
    try {
      await save();
      const { data } = await api.post(`/attendance/${dayId}/send-followups`, { roster_ids: recipients.map((r) => r.roster_id) });
      toast.success(`Sent ${data.sent} follow-up email${data.sent === 1 ? "" : "s"}`);
      if (data.errors?.length) toast.error(`${data.errors.length} failed — see logs`);
      loadAttendance(dayId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send follow-ups");
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const letter = r.team_letter || "Unassigned";
      if (!g[letter]) g[letter] = [];
      g[letter].push(r);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const toggle = (rid) => {
    // Legacy tap — cycle through the two most common states.
    setRows((prev) => prev.map((r) => {
      if (r.roster_id !== rid) return r;
      const next = r.state === "present" ? "" : "present";
      return { ...r, state: next, present: next === "present" };
    }));
  };

  const setField = (rid, key, val) => {
    setRows((prev) => prev.map((r) =>
      r.roster_id === rid ? { ...r, [key]: val } : r));
  };

  const save = async () => {
    if (!dayId) return;
    setSaving(true);
    try {
      await api.post(`/attendance/${dayId}/bulk`, {
        records: rows.map((r) => ({
          roster_id: r.roster_id,
          state: r.state || "",
          present: ["present","late","left_early"].includes(r.state),
          food_order: r.food_order || null,
          notes: r.notes || null,
        })),
      });
      toast.success("Attendance saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save attendance");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    if (!dayId) return;
    try {
      const res = await api.get(`/attendance/${dayId}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const num = dayMeta?.day_number || dayId.slice(0, 8);
      a.download = `attendance-session-${num}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not export attendance");
    }
  };

  const openFoodSummary = async () => {
    try {
      const { data } = await api.get(`/attendance/${dayId}/food-summary`);
      setSummary(data);
      setShowSummary(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load food summary");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="attendance-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Attendance &amp; Food Orders</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Tick each student off after the session. Add their food order — the
          summary rolls up allergies and totals for the caterer.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 p-4 rounded-lg bg-slate-50 border border-slate-200">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-600">Session</label>
          <select
            data-testid="attendance-session-select"
            value={dayId}
            onChange={(e) => setDayId(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
          >
            {days.map((d) => (
              <option key={d.id} value={d.id}>
                Session {d.day_number} · {d.title} · {d.date}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[120px]" />
        <Button
          data-testid="attendance-mark-all-present-btn"
          variant="outline"
          onClick={markAllPresent}
        >
          Mark all present
        </Button>
        <Button
          data-testid="attendance-clear-all-btn"
          variant="outline"
          onClick={clearAll}
        >
          Clear all
        </Button>
        <Button
          data-testid="attendance-followups-btn"
          variant="outline"
          onClick={sendFollowups}
          disabled={stats.pendingFollowups === 0}
          className={stats.pendingFollowups > 0 ? "border-rose-500 text-rose-700 hover:bg-rose-50" : ""}
        >
          <Mail className="w-4 h-4 mr-1.5" />
          {stats.pendingFollowups > 0
            ? `Send ${stats.pendingFollowups} no-show follow-up${stats.pendingFollowups === 1 ? "" : "s"}`
            : "No follow-ups pending"}
        </Button>
        <Button
          data-testid="attendance-food-summary-btn"
          variant="outline"
          onClick={openFoodSummary}
        >
          <Utensils className="w-4 h-4 mr-1.5" /> Food summary
        </Button>
        <Button
          data-testid="attendance-export-csv-btn"
          variant="outline"
          onClick={exportCsv}
        >
          <FileDown className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
        <Button
          data-testid="attendance-save-btn"
          onClick={() => save().catch(() => {})}
          disabled={saving}
        >
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save attendance"}
        </Button>
      </div>

      <div className="grid grid-cols-5 gap-3 mb-6">
        <StatCard label="Total on roster" value={stats.total} />
        <StatCard label="Present / late" value={stats.present} tone="green" />
        <StatCard label="Absent" value={stats.absent} tone="red" />
        <StatCard label="No-shows" value={stats.noShows} tone="red" />
        <StatCard label="Orders logged" value={stats.withOrders} tone="amber" />
      </div>

      {loading ? (
        <div className="text-center text-slate-400 py-12">Loading attendance…</div>
      ) : rows.length === 0 ? (
        <div className="text-center text-slate-400 py-12">
          The roster is empty — import a student list from the Roster page first.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([letter, teamRows]) => {
            const teamName = (teamRows[0] || {}).team_name;
            return (
              <div key={letter} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold">
                    {letter === "Unassigned" ? "?" : letter}
                  </div>
                  <div>
                    <div className="font-semibold">
                      {letter === "Unassigned" ? "Not yet on a team" : `Team ${letter}`}
                    </div>
                    {teamName && (
                      <div className="text-xs text-slate-500">{teamName}</div>
                    )}
                  </div>
                  <div className="ml-auto text-sm text-slate-500">
                    {teamRows.filter((r) => r.present).length}/{teamRows.length} present
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {teamRows.map((r) => (
                    <div
                      key={r.roster_id}
                      className="grid grid-cols-12 gap-3 items-center px-4 py-3"
                      data-testid={`attendance-row-${r.roster_id}`}
                    >
                      <div className="col-span-3">
                        <div className="font-medium flex items-center gap-2">
                          {r.full_name}
                          {r.roster_status === "withdrew" && (
                            <span className="text-[10px] uppercase bg-rose-100 text-rose-800 px-1.5 py-0.5 border border-rose-300">withdrew</span>
                          )}
                        </div>
                        {r.email && <div className="text-xs text-slate-500 truncate">{r.email}</div>}
                      </div>
                      <div className="col-span-3">
                        <select
                          value={r.state || ""}
                          onChange={(e) => setState(r.roster_id, e.target.value)}
                          data-testid={`attendance-state-${r.roster_id}`}
                          className={`w-full h-9 border rounded px-2 text-sm ${
                            STATE_TONE[r.state] === "rose" ? "border-rose-400 bg-rose-50" :
                            STATE_TONE[r.state] === "amber" ? "border-amber-400 bg-amber-50" :
                            STATE_TONE[r.state] === "emerald" ? "border-emerald-400 bg-emerald-50" :
                            "border-slate-300 bg-white"
                          }`}
                        >
                          {STATES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                        {r.state === "no_show" && (
                          <div className="mt-1 text-[10px] flex items-center gap-1 text-rose-700">
                            <AlertTriangle className="w-3 h-3" />
                            {r.followup_sent_at
                              ? `Follow-up sent ${new Date(r.followup_sent_at).toLocaleDateString()}`
                              : "Follow-up pending — save then click send"}
                          </div>
                        )}
                      </div>
                      <div className="col-span-2 text-xs">
                        {r.dietary && r.dietary !== "None" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            <Utensils className="w-3 h-3" /> {r.dietary}
                          </span>
                        ) : (
                          <span className="text-slate-400">No dietary notes</span>
                        )}
                      </div>
                      <div className="col-span-4">
                        <Input
                          data-testid={`attendance-food-${r.roster_id}`}
                          placeholder="Food order (student's pick or type manually)"
                          value={r.food_order || ""}
                          onChange={(e) => setField(r.roster_id, "food_order", e.target.value)}
                          className="h-9"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showSummary && summary && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowSummary(false)}
          data-testid="attendance-food-summary-modal"
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Food summary</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const text = renderSummaryText(summary, dayMeta);
                  navigator.clipboard.writeText(text);
                  toast.success("Copied to clipboard — paste into the caterer email");
                }}
                data-testid="attendance-copy-summary-btn"
              >
                <Download className="w-4 h-4 mr-1.5" /> Copy for caterer
              </Button>
            </div>
            <SummarySection title="Order totals" data={summary.order_counts} />
            <SummarySection title="Dietary breakdown" data={summary.dietary_counts} />
            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Individual breakdown</div>
              <div className="max-h-64 overflow-y-auto rounded border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-2">Team</th>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Dietary</th>
                      <th className="px-3 py-2">Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.breakdown.map((b, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold">{b.team_letter || "—"}</td>
                        <td className="px-3 py-2">{b.name}</td>
                        <td className="px-3 py-2 text-slate-600">{b.dietary}</td>
                        <td className="px-3 py-2 text-slate-600">{b.food_order}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-6 text-right">
              <Button onClick={() => setShowSummary(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneClass = {
    green: "text-emerald-700 bg-emerald-50 border-emerald-200",
    red: "text-rose-700 bg-rose-50 border-rose-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
  }[tone] || "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

function SummarySection({ title, data }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <div className="mb-4">
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-sm">
            <strong>{v}</strong>&nbsp;·&nbsp;{k}
          </span>
        ))}
      </div>
    </div>
  );
}

function renderSummaryText(summary, day) {
  const header = day ? `Session ${day.day_number} · ${day.title} · ${day.date}\n` : "";
  const orders = Object.entries(summary.order_counts).sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `  ${v} × ${k}`).join("\n");
  const diets = Object.entries(summary.dietary_counts).sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `  ${v} × ${k}`).join("\n");
  return `${header}\nFood orders\n${orders}\n\nDietary requirements\n${diets}\n`;
}
